import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Observable, throwError } from 'rxjs'
import { catchError, tap } from 'rxjs/operators'

import { SnackBarService } from '@app/core/modules/snack-bar/snack-bar.service'

import { FEEDBACK_ON_ERROR, SHOW_FEEDBACK, SUCCESS_MESSAGE } from './feedback-context'

/**
 * Opt-in HTTP interceptor that shows success/error snackbar for mutation requests.
 *
 * Default OFF: a request only triggers feedback if `SHOW_FEEDBACK` HttpContext token is true.
 * This prevents false toasts for POST/PATCH endpoints used as data fetchers (vd pagination,
 * exportFile, sensor history, UI state save).
 *
 * 401 is left for AuthInterceptor to handle silently (token refresh + retry).
 */
@Injectable()
export class FormFeedbackInterceptor implements HttpInterceptor {
  constructor(
    private readonly snackBar: SnackBarService,
    private readonly translate: TranslateService,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!request.context.get(SHOW_FEEDBACK)) {
      return next.handle(request)
    }

    return next.handle(request).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          this.showSuccess(request, event)
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // 401 belongs to AuthInterceptor (refresh token + retry). Stay silent to avoid
        // double-toast (error toast on first try → success toast after retry).
        if (error.status === 401) {
          return throwError(() => error)
        }
        // Skip the error toast for success-only callers that already surface errors
        // themselves (vd a confirm dialog or an inline form error).
        if (request.context.get(FEEDBACK_ON_ERROR)) {
          this.showError(error)
        }
        return throwError(() => error)
      }),
    )
  }

  private showSuccess(request: HttpRequest<any>, response: HttpResponse<any>): void {
    const customKey = request.context.get(SUCCESS_MESSAGE)
    const message = customKey
      ? this.translate.instant(customKey)
      : (response.body?.message ?? this.translate.instant('COMMON.SAVED_SUCCESSFULLY'))
    this.snackBar.success(message)
  }

  private showError(error: HttpErrorResponse): void {
    if (error.status >= 400 && error.status < 500) {
      // BE error envelope shapes — verified against Go DEV + PHP, see
      // local-docs/api-specs/go-error-envelope-spec.md:
      //   PHP legacy / Go /login:  { errors: [{ detail }] }  (detail = string, OR on PHP validation
      //                                                        a field-keyed object { field: { rule } })
      //   Go business 4xx:         { error: 'Client not found' }   (single string — the common Go shape)
      //   Alt:                     { message: '...' }
      // Guard: only take errors[0].detail when it is a string. A PHP field-keyed object detail is
      // surfaced inline by handleFormErrors(), so here we fall through to a generic toast rather than
      // rendering '[object Object]'.
      const detail = error.error?.errors?.[0]?.detail
      const message =
        (typeof detail === 'string' ? detail : undefined) ??
        error.error?.error ??
        error.error?.message ??
        this.translate.instant('COMMON.SAVE_FAILED')
      this.snackBar.error(message === 'IMPERSONATION_RESTRICTED' ? this.translate.instant('COMMON.IMPERSONATION_RESTRICTED') : message)
    } else if (error.status >= 500) {
      this.snackBar.error(this.translate.instant('COMMON.SERVER_ERROR'))
    } else {
      // status 0 (network error / CORS / aborted)
      this.snackBar.error(this.translate.instant('COMMON.SAVE_FAILED'))
    }
  }
}
